import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { Helmet } from 'react-helmet-async';
import AuthGuard from '../components/AuthGuard';
import AdminNavigationModern from '../components/AdminNavigationModern';
import AdminDashboard from '../components/admin/AdminDashboard';
import EmailStatusChecker from '../components/admin/EmailStatusChecker';
import VehicleManager from '../components/admin/VehicleManager';
import DealManagement from '../components/admin/DealManagement';
import ServiceAppointmentManager from '../components/admin/ServiceAppointmentManager';
import LeadManagement from '../components/admin/LeadManagement';
import PaymentManagement from '../components/admin/PaymentManagement';
import AdminSettings from '../components/admin/AdminSettings';
import AdminAnalytics from '../components/admin/AdminAnalytics';
import AIChatAdmin from '../components/admin/AIChatAdmin';
import SMSManager from '../components/admin/SMSManager';
import TwoFactorSetup from '../components/admin/TwoFactorSetup';
import AuthorizeNetTest from '../components/AuthorizeNetTest';
import ContractDashboard from '../components/admin/ContractDashboard';
import FormsManager from '../components/admin/FormsManager';
import DealBuilder from '../components/admin/DealBuilder';
import CarGurusComplete from '../components/admin/CarGurusComplete';
import CreditApplications from '../components/admin/CreditApplications';
import CreditBureauSettings from '../components/admin/CreditBureauSettings';
import SubscriptionManager from '../components/admin/SubscriptionManager';
import CreditCheckManager from '../components/admin/CreditCheckManager';
import CreditConsentDashboard from '../components/admin/CreditConsentDashboard';
import VINDecoder from './VINDecoder';
// import { auditService } from '../services/auditService'; // Temporarily disabled

const Admin = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { user } = useAuth();
  
  // Session timeout with 30 minute timeout, 5 minute warning
  useSessionTimeout(30, 5);
  
  // Log admin access
  useEffect(() => {
    if (user) {
      // auditService.logAdminAction('admin_panel_access', {
      //   email: user.email,
      //   page: 'admin_dashboard'
      // });
    }
  }, [user]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard setActiveTab={setActiveTab} />;
      case 'vehicles':
        return <VehicleManager unitType="vehicle" />;
      case 'golf-carts':
        return <VehicleManager unitType="golf-cart" />;
      case 'trailers':
        return <VehicleManager unitType="trailer" />;
      case 'boats':
        return <VehicleManager unitType="boat" />;
      case 'deals':
        return <DealManagement />;
      case 'deal-builder':
        return <DealBuilder />;
      case 'forms':
        return <FormsManager />;
      case 'credit-apps':
        return <CreditApplications />;
      case 'credit-settings':
        return <CreditBureauSettings />;
      case 'credit-check':
        return <CreditCheckManager />;
      case 'credit-consent':
        return <CreditConsentDashboard />;
      case 'contracts':
        return <ContractDashboard />;
      case 'service':
        return <ServiceAppointmentManager />;
      case 'leads':
        return <LeadManagement />;
      case 'payments':
        return <PaymentManagement />;
      case 'subscriptions':
        return <SubscriptionManager />;
      case 'payment-test':
        return <AuthorizeNetTest />;
      case 'sms':
        return <SMSManager />;
      case 'security':
        return <TwoFactorSetup />;
      case 'ai-chat':
        return <AIChatAdmin />;
      case 'analytics':
        return <AdminAnalytics />;
      case 'cargurus':
        return <CarGurusComplete />;
      case 'vin-decoder':
        return <VINDecoder />;
      case 'google-vla':
        return <div className="p-8 text-center text-gray-500">Feed is manually managed via Google Merchant Center. No generator needed.</div>;
      case 'settings':
        return <AdminSettings />;
      default:
        return <AdminDashboard setActiveTab={setActiveTab} />;
    }
  };

  return (
    <AuthGuard>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gray-50 dark-mode:bg-slate-900 transition-colors duration-300"
      >
        <Helmet>
          <title>Admin Dashboard - Paradise-Performance</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <AdminNavigationModern activeTab={activeTab} setActiveTab={setActiveTab} />
        
        {/* Security Alert for 2FA */}
        {user && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Security Notice:</strong> Two-factor authentication is available. 
              <button 
                onClick={() => setActiveTab('security')}
                className="underline hover:text-blue-800 ml-1"
              >
                Set up 2FA
              </button>
            </p>
          </div>
        )}
        
        {/* Add padding-top to account for fixed header */}
        <div className="flex pt-16 overflow-x-hidden">
          <main className="flex-1 min-w-0 p-4 sm:p-6">
            {/* Email configuration warning */}
            <EmailStatusChecker />
            {renderContent()}
          </main>
        </div>
      </motion.div>
    </AuthGuard>
  );
};

export default Admin;